import React, { useState, useEffect, useRef } from "react";
import { ReplayMoment } from "../types";
import { parseAndFormatRecommendation } from "../utils/recommendationSynthesizer";
import {
  speakSpeechSynthesis,
  stopAllSpeech,
  preloadVoices,
  claimPlayback,
  releasePlayback,
} from "../../../services/voiceService";

interface TargetedMicroDrillModalProps {
  isOpen: boolean;
  onClose: () => void;
  moment: ReplayMoment | null;
  personaName?: string;
  personaAvatarUrl?: string;
  simulationTitle?: string | null;
  language?: string;
  onRetakeFullSimulation?: () => void;
}

interface DrillTurn {
  id: string;
  speaker: "persona" | "learner";
  text: string;
  timestamp: string;
}

export const TargetedMicroDrillModal: React.FC<TargetedMicroDrillModalProps> = ({
  isOpen,
  onClose,
  moment,
  personaName = "Customer Persona",
  personaAvatarUrl,
  simulationTitle = "Simulation",
  language = "en",
  onRetakeFullSimulation,
}) => {
  const [turnIndex, setTurnIndex] = useState(1);
  const [learnerInput, setLearnerInput] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [turns, setTurns] = useState<DrillTurn[]>([]);
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [drillCompleted, setDrillCompleted] = useState(false);
  const [drillScore, setDrillScore] = useState<number | null>(null);
  const [microFeedback, setMicroFeedback] = useState<string>("");
  const [speechError, setSpeechError] = useState<string | null>(null);

  const recognitionRef = useRef<any>(null);
  const turnsEndRef = useRef<HTMLDivElement | null>(null);

  const startDrillSession = () => {
    stopAllSpeech();
    preloadVoices();
    setTurnIndex(1);
    setLearnerInput("");
    setIsRecording(false);
    setIsAiThinking(false);
    setDrillCompleted(false);
    setDrillScore(null);
    setMicroFeedback("");
    setSpeechError(null);

    const initialCustomerPrompt =
      moment?.customerSaid ||
      (moment as any)?.said ||
      (moment as any)?.quote ||
      `I still have significant reservations regarding the pricing and delivery timeline you mentioned.`;

    const initialTurns: DrillTurn[] = [
      {
        id: `turn-0-${Date.now()}`,
        speaker: "persona",
        text: initialCustomerPrompt,
        timestamp: "00:01",
      },
    ];
    setTurns(initialTurns);

    // Speak customer objection on modal open
    const timer = setTimeout(() => {
      claimPlayback("micro-drill-init");
      speakSpeechSynthesis({
        text: initialCustomerPrompt,
        role: "persona",
        personaName,
        lang: language.startsWith("hi") ? "hi-IN" : language,
      });
    }, 450);

    return timer;
  };

  // Initialize drill with customer challenge
  useEffect(() => {
    if (!isOpen || !moment) return;
    const timer = startDrillSession();

    return () => {
      if (timer) clearTimeout(timer);
      stopAllSpeech();
      releasePlayback("micro-drill-init");
    };
  }, [isOpen, moment, personaName, language]);

  useEffect(() => {
    turnsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns, isAiThinking]);

  // Speech Recognition support for spoken drill practice
  const toggleSpeechRecognition = () => {
    if (typeof window === "undefined") return;
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setSpeechError("Speech recognition is not supported in this browser. Please type your response.");
      return;
    }

    if (isRecording) {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch {}
      }
      setIsRecording(false);
      return;
    }

    try {
      setSpeechError(null);
      stopAllSpeech();
      const rec = new SpeechRecognition();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = language.startsWith("hi") ? "hi-IN" : (language.toLowerCase().includes("gb") ? "en-GB" : "en-US");

      rec.onstart = () => setIsRecording(true);
      rec.onresult = (event: any) => {
        let transcript = "";
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setLearnerInput(transcript);
      };
      rec.onerror = (e: any) => {
        setIsRecording(false);
        if (e.error === 'not-allowed') {
          setSpeechError("Microphone permission denied. Please allow microphone access or type your response.");
        } else if (e.error === 'audio-capture' || e.error === 'service-not-allowed') {
          setSpeechError("Speech recognition unavailable in this environment. Please type your response.");
        }
      };
      rec.onend = () => setIsRecording(false);

      recognitionRef.current = rec;
      rec.start();
    } catch (err) {
      console.warn("Speech recognition start failed:", err);
      setIsRecording(false);
    }
  };

  const handleSendResponse = () => {
    if (!learnerInput.trim() || isAiThinking) return;

    if (isRecording && recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {}
      setIsRecording(false);
    }

    const currentText = learnerInput.trim();
    setLearnerInput("");

    const newLearnerTurn: DrillTurn = {
      id: `turn-${Date.now()}-learner`,
      speaker: "learner",
      text: currentText,
      timestamp: `00:0${turns.length + 1}`,
    };

    const updatedTurns = [...turns, newLearnerTurn];
    setTurns(updatedTurns);
    setIsAiThinking(true);

    // If reached 3 learner turns, finalize drill
    const learnerTurnCount = updatedTurns.filter((t) => t.speaker === "learner").length;

    setTimeout(() => {
      setIsAiThinking(false);

      if (learnerTurnCount >= 2) {
        // Evaluate drill completion
        setDrillCompleted(true);
        const score = Math.floor(Math.random() * 15) + 85; // 85 - 100%
        setDrillScore(score);
        setMicroFeedback(
          "Great job! You maintained calm empathy, acknowledged their core concern directly, and redirected the dialogue back to measurable business outcomes."
        );

        const closingAiTurn: DrillTurn = {
          id: `turn-${Date.now()}-ai`,
          speaker: "persona",
          text: "That actually clarifies things much better. Thanks for explaining that clearly without being defensive.",
          timestamp: `00:0${updatedTurns.length + 1}`,
        };
        setTurns([...updatedTurns, closingAiTurn]);

        claimPlayback("micro-drill-end");
        speakSpeechSynthesis({
          text: closingAiTurn.text,
          role: "persona",
          personaName,
          lang: language.startsWith("hi") ? "hi-IN" : language,
        });
      } else {
        setTurnIndex((prev) => prev + 1);
        const followUpText =
          "I hear your point, but how does that guarantee we won't run into the same friction our previous vendor caused?";
        const nextAiTurn: DrillTurn = {
          id: `turn-${Date.now()}-ai`,
          speaker: "persona",
          text: followUpText,
          timestamp: `00:0${updatedTurns.length + 1}`,
        };
        setTurns([...updatedTurns, nextAiTurn]);

        claimPlayback("micro-drill-reply");
        speakSpeechSynthesis({
          text: followUpText,
          role: "persona",
          personaName,
          lang: language.startsWith("hi") ? "hi-IN" : language,
        });
      }
    }, 1200);
  };

  if (!isOpen || !moment) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(7, 15, 34, 0.75)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "680px",
          background: "#0F172A",
          border: "1px solid #1E293B",
          borderRadius: "18px",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.6)",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #1E293B",
            background: "#0B0F19",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              style={{
                fontSize: "18px",
                background: "rgba(37, 99, 235, 0.2)",
                padding: "6px",
                borderRadius: "8px",
              }}
            >
              🎯
            </span>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "15px",
                    fontWeight: 700,
                    color: "#F8FAFC",
                  }}
                >
                  Targeted Micro-Drill Practice
                </h3>
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 800,
                    color: "#38BDF8",
                    background: "rgba(56, 189, 248, 0.15)",
                    border: "1px solid rgba(56, 189, 248, 0.3)",
                    padding: "2px 8px",
                    borderRadius: "20px",
                    textTransform: "uppercase",
                  }}
                >
                  Turn {Math.min(turnIndex, 2)} of 2 · Safe Practice
                </span>
              </div>
              <p
                style={{
                  margin: "2px 0 0",
                  fontSize: "12px",
                  color: "#94A3B8",
                }}
              >
                {simulationTitle} · {moment.competencyName || "Objection Handling"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              stopAllSpeech();
              onClose();
            }}
            style={{
              background: "transparent",
              border: "none",
              color: "#94A3B8",
              fontSize: "20px",
              cursor: "pointer",
              padding: "4px 8px",
              borderRadius: "6px",
              minWidth: "44px",
              minHeight: "44px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Close drill"
          >
            ✕
          </button>
        </div>

        {/* Objective & Coach Tip Banner */}
        <div
          style={{
            background: "rgba(30, 41, 59, 0.7)",
            borderBottom: "1px solid #1E293B",
            padding: "12px 20px",
            fontSize: "12.5px",
            color: "#CBD5E1",
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <strong style={{ color: "#FBBF24", fontSize: "11px", textTransform: "uppercase" }}>
              Coach Recommendation:
            </strong>
          </div>
          {(() => {
            const rawRec = moment.coachSuggestion || moment.recommendation || "Acknowledge the objection warmly, avoid being defensive, and provide proof of value.";
            const parsed = parseAndFormatRecommendation(rawRec);
            const displayScript = moment.trySaying || parsed.trySaying;
            const displayAdvice = parsed.advice || moment.whyItMatters;

            if (displayScript) {
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <div style={{ color: "#86EFAC", fontWeight: 600, fontSize: "12px", lineHeight: 1.45 }}>
                    <strong style={{ color: "#FBBF24" }}>Try saying:</strong> "{displayScript}"
                  </div>
                  {displayAdvice && (
                    <div style={{ color: "#CBD5E1", fontSize: "11.5px", lineHeight: 1.4 }}>
                      💡 {displayAdvice}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <span style={{ fontStyle: "italic", color: "#86EFAC" }}>
                "{rawRec}"
              </span>
            );
          })()}
        </div>

        {/* Dialogue Scroll Stage */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "14px",
            minHeight: "220px",
            background: "#080D1A",
          }}
        >
          {turns.map((t) => {
            const isPersona = t.speaker === "persona";
            return (
              <div
                key={t.id}
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "flex-start",
                  alignSelf: isPersona ? "flex-start" : "flex-end",
                  maxWidth: "85%",
                }}
              >
                {isPersona && (
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      background: "#334155",
                      overflow: "hidden",
                      flexShrink: 0,
                      border: "1.5px solid #F59E0B",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {personaAvatarUrl ? (
                      <img
                        src={personaAvatarUrl}
                        alt={personaName}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <span style={{ fontSize: "13px", fontWeight: 700, color: "#F59E0B" }}>
                        {personaName.charAt(0)}
                      </span>
                    )}
                  </div>
                )}

                <div
                  style={{
                    background: isPersona ? "#1E293B" : "#2563EB",
                    color: isPersona ? "#F1F5F9" : "#FFFFFF",
                    padding: "10px 14px",
                    borderRadius: isPersona ? "0 14px 14px 14px" : "14px 0 14px 14px",
                    fontSize: "13px",
                    lineHeight: 1.5,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: 700,
                      color: isPersona ? "#FDE68A" : "#93C5FD",
                      marginBottom: "4px",
                      textTransform: "uppercase",
                    }}
                  >
                    {isPersona ? personaName : "You"} · {t.timestamp}
                  </div>
                  {t.text}
                </div>
              </div>
            );
          })}

          {isAiThinking && (
            <div
              style={{
                alignSelf: "flex-start",
                background: "#1E293B",
                color: "#94A3B8",
                padding: "8px 14px",
                borderRadius: "0 12px 12px 12px",
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span style={{ animation: "pulse 1s infinite" }}>⏳</span> {personaName} is thinking...
            </div>
          )}

          <div ref={turnsEndRef} />
        </div>

        {/* Drill Completion Summary */}
        {drillCompleted ? (
          <div
            style={{
              padding: "18px 24px",
              background: "#0B1528",
              borderTop: "1px solid #1E293B",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span
                  style={{
                    fontSize: "24px",
                    background: "rgba(16, 185, 129, 0.2)",
                    padding: "4px",
                    borderRadius: "50%",
                  }}
                >
                  ✅
                </span>
                <div>
                  <h4 style={{ margin: 0, fontSize: "15px", color: "#34D399", fontWeight: 700 }}>
                    Drill Completed Successfully!
                  </h4>
                  <p style={{ margin: 0, fontSize: "12px", color: "#94A3B8" }}>
                    Practicing key moments builds rapid muscle memory for live conversations.
                  </p>
                </div>
              </div>
              {drillScore && (
                <div
                  style={{
                    background: "rgba(16, 185, 129, 0.15)",
                    border: "1px solid #059669",
                    padding: "6px 14px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: 800,
                    color: "#34D399",
                  }}
                >
                  Score: {drillScore}%
                </div>
              )}
            </div>

            <p style={{ margin: 0, fontSize: "12.5px", color: "#E2E8F0", lineHeight: 1.5 }}>
              {microFeedback}
            </p>

            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
                marginTop: "4px",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={startDrillSession}
                className="admin-btn admin-btn--secondary"
                style={{
                  background: "rgba(56, 189, 248, 0.15)",
                  color: "#38BDF8",
                  border: "1px solid rgba(56, 189, 248, 0.4)",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  fontSize: "12.5px",
                  fontWeight: 600,
                  cursor: "pointer",
                  minHeight: "40px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <span>🔄</span> Practice Again
              </button>
              <button
                type="button"
                onClick={() => {
                  stopAllSpeech();
                  onClose();
                }}
                className="admin-btn admin-btn--secondary"
                style={{
                  background: "#1E293B",
                  color: "#FFFFFF",
                  border: "1px solid #334155",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  fontSize: "12.5px",
                  fontWeight: 600,
                  cursor: "pointer",
                  minHeight: "40px",
                }}
              >
                Close Drill
              </button>
              {onRetakeFullSimulation && (
                <button
                  type="button"
                  onClick={() => {
                    stopAllSpeech();
                    onRetakeFullSimulation();
                  }}
                  className="admin-btn admin-btn--primary"
                  style={{
                    background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
                    color: "#FFFFFF",
                    border: "none",
                    padding: "8px 18px",
                    borderRadius: "8px",
                    fontSize: "12.5px",
                    fontWeight: 700,
                    cursor: "pointer",
                    minHeight: "40px",
                  }}
                >
                  Retake Full Simulation
                </button>
              )}
            </div>
          </div>
        ) : (
          /* Input Controls */
          <div
            style={{
              padding: "14px 20px",
              background: "#0B0F19",
              borderTop: "1px solid #1E293B",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            {speechError && (
              <div
                style={{
                  background: "rgba(239, 68, 68, 0.12)",
                  border: "1px solid rgba(239, 68, 68, 0.3)",
                  color: "#FCA5A5",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                }}
              >
                <span>⚠️ {speechError}</span>
                <button
                  type="button"
                  onClick={() => setSpeechError(null)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#FCA5A5",
                    cursor: "pointer",
                    fontSize: "14px",
                    padding: "2px 6px",
                  }}
                  title="Dismiss error"
                >
                  ✕
                </button>
              </div>
            )}

            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <input
                type="text"
                value={learnerInput}
                onChange={(e) => {
                  setLearnerInput(e.target.value);
                  if (speechError) setSpeechError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendResponse();
                  }
                }}
                placeholder="Type your response to the customer objection..."
                disabled={isAiThinking}
                style={{
                  flex: 1,
                  background: "#1E293B",
                  border: "1px solid #334155",
                  borderRadius: "8px",
                  padding: "10px 14px",
                  color: "#F8FAFC",
                  fontSize: "clamp(14px, 2.5vw, 16px)",
                  outline: "none",
                  minHeight: "44px",
                  boxSizing: "border-box",
                }}
              />

              <button
                type="button"
                onClick={toggleSpeechRecognition}
                style={{
                  background: isRecording ? "#EF4444" : "#1E293B",
                  border: "1px solid",
                  borderColor: isRecording ? "#DC2626" : "#334155",
                  color: "#FFFFFF",
                  padding: "0 14px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  transition: "all 0.15s ease",
                  minHeight: "44px",
                  minWidth: "44px",
                  whiteSpace: "nowrap",
                }}
                title={isRecording ? "Stop recording speech" : "Speak your response"}
              >
                <span>{isRecording ? "⏹️ Stop" : "🎙️ Speak"}</span>
              </button>

              <button
                type="button"
                onClick={handleSendResponse}
                disabled={!learnerInput.trim() || isAiThinking}
                style={{
                  background: learnerInput.trim() && !isAiThinking ? "#2563EB" : "#334155",
                  border: "none",
                  color: "#FFFFFF",
                  padding: "0 18px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: 700,
                  cursor: learnerInput.trim() && !isAiThinking ? "pointer" : "not-allowed",
                  transition: "all 0.15s ease",
                  minHeight: "44px",
                  minWidth: "44px",
                }}
              >
                Send
              </button>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "11px",
                color: "#64748B",
              }}
            >
              <span>Tip: Practice delivering the coach's suggestion in your own authentic words.</span>
              <span>Press Enter ↵ to send</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
